import React from 'react';
import { GatsbyLinkProps } from 'gatsby';
import { AnchorLink } from 'gatsby-plugin-anchor-links';
import { styled, useTheme, Theme, useMediaQuery } from '@mui/material';
import Button, { Props } from '../button';
import { useGlobalState } from '../GlobalState';

type HeaderLinkProps = Omit<GatsbyLinkProps<Record<string, unknown>>, 'ref'> & {
  variant?: Props['variant'];
  color?: Props['color'];
  startIcon?: Props['startIcon'];
  theme?: Theme;
  mobile?: boolean;
};
const CustomLink = styled(AnchorLink)`
  position: relative;
  text-decoration: none;
  &.active:after,
  .active:after {
    content: '';
    background: ${(props: HeaderLinkProps) =>
      props.theme?.palette.greyscale.white};
    height: 4px;
    width: 90%;
    position: absolute;
    left: 2px;
    right: 0;
    margin: auto;
    bottom: -18px;

    @media (max-width: ${({ theme }) => theme.breakpoints.values.md}px) {
      height: 2px;
      width: 80%;
      left: 0;
      bottom: -12px;
    }
  }
  &.mobile {
    width: 100%;
  }
  &.mobile.active:after,
  .mobile.active:after {
    display: none;
  }
  &.mobile.active:before,
  .mobile.active:before {
    content: '';
    background: ${(props: HeaderLinkProps) =>
      props.theme?.palette.greyscale['05']};
    width: 2px;
    height: calc(100%);
    position: absolute;
    top: 0;
    bottom: 0;
    left: -1rem;
    margin: auto;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.values.md}px) {
    &.mobile.active {
      .MuiButton-root {
        color: ${({ theme }) => theme.palette.greyscale.white};
      }
    }
  }
`;

const CustomButton = styled(Button)`
  padding: 6px 18px;
  min-width: max-content;

  &.MuiButton-root {
    .MuiButton-startIcon {
      margin-left: 8px;
    }
    text-decoration: none;
    letter-spacing: 2px;
    background: none;
    &:hover,
    &:active,
    &:focus,
    &:visited,
    &:focus-visible {
      background: none;
      color: inherit;
    }

    svg {
      font-size: 24px;
      color: ${({ theme }) => theme.palette.greyscale.white};
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.values.md}px) {
    &.MuiButton-root {
      font-size: 10px;
      letter-spacing: 1.2px;
      padding: 6px;
      color: ${({ theme }) => theme.palette.greyscale['05']};
    }
  }
`;

const HeaderLink: React.FC<HeaderLinkProps> = ({
  title,
  variant,
  color,
  startIcon,
  to,
  children,
  mobile,
  ...props
}) => {
  const [{ currentPage }, dispatch] = useGlobalState();
  const theme = useTheme();
  const isMobile = useMediaQuery(
    `(max-width: ${theme.breakpoints.values.md}px)`,
  );
  return (
    <CustomLink
      to={to}
      theme={theme}
      title={title}
      className={`${mobile ? 'mobile' : ''} ${
        currentPage.includes(to) ? 'active' : ''
      } header-link`}
      gatsbyLinkProps={{ activeClassName: `${isMobile ? '' : 'active'}` }}
      onAnchorLinkClick={() => {
        dispatch({ type: 'PAGE_SET_CURRENT', currentPage: to });
      }}
      {...props}
    >
      {children ?? (
        <CustomButton
          style={{ width: 'auto', fontSize: '14px', color: 'white' }}
          variant={variant ?? 'ghost'}
          color={color ?? 'white'}
          startIcon={startIcon}
          text={title}
        />
      )}
    </CustomLink>
  );
};

export default HeaderLink;
